import { useEffect, useRef } from "react";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { FiArrowRight, FiAward, FiExternalLink, FiX } from "react-icons/fi";
import { CERTIFICATES } from "../../data/profile";
import { certificateMeta } from "../../data/pageMeta";
import { applyPageMeta } from "../../usePageMeta";
import "./Certificates.css";
import { lockStoryScroll } from "../../scroll/scroll";

const completionDate = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-IE", {
  day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
});

function CertificateDialog({ certificate, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const unlock = lockStoryScroll();
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    applyPageMeta(certificateMeta(certificate));
    document.body.style.overflow = "hidden";
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      unlock();
      applyPageMeta();
      opener?.focus?.({ preventScroll: true });
    };
  }, [certificate]);

  return (
    <dialog
      ref={dialogRef}
      className="cert-dialog"
      data-lenis-prevent
      aria-labelledby="certificate-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="cert-dialog-content">
        <button type="button" className="cert-close" onClick={onClose} aria-label="Close certificate details" autoFocus>
          <FiX aria-hidden="true" />
        </button>
        <p className="cert-eyebrow">Certificate of completion</p>
        <h2 id="certificate-title">{certificate.title}</h2>
        <p className="cert-meta">{certificate.issuer} · <time dateTime={certificate.completed}>{completionDate(certificate.completed)}</time></p>
        <p className="cert-meta">{certificate.detail}</p>
        <img className="cert-preview" src={`/certificates/${certificate.slug}.png`} alt={`Certificate awarded to Diarmuid Hession for completing ${certificate.title}`} />
        <a className="btn-brand" href={`/certificates/${certificate.slug}.pdf`} target="_blank" rel="noopener noreferrer">
          Open certificate PDF <FiExternalLink className="icon-out" aria-hidden="true" />
        </a>
      </div>
    </dialog>
  );
}

export default function Certificates() {
  const match = useMatch("/certificates/:slug");
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const selected = CERTIFICATES.find((certificate) => certificate.slug === match?.params.slug);

  useEffect(() => {
    if (match && !selected) navigate("/", { replace: true });
    if (selected) sectionRef.current?.scrollIntoView({ block: "start" });
  }, [match, selected, navigate]);

  return (
    <section ref={sectionRef} id="certificates" className="certificates" aria-labelledby="certificates-title">
      <div className="cert-head" data-reveal>
        <h3 id="certificates-title">Certificates</h3>
        <p className="cert-intro">Short courses in software requirements, testing and Git.</p>
      </div>
      <div className="cert-grid">
        {CERTIFICATES.map((certificate, i) => (
          <div key={certificate.slug} className="cert-cell" data-reveal style={{ "--i": i % 2 }}>
            <Link to={`/certificates/${certificate.slug}`} className="cert-card">
              <FiAward className="cert-icon" aria-hidden="true" />
              <div>
                <h4>{certificate.title}</h4>
                <p className="cert-meta">{certificate.issuer}</p>
                <time className="cert-meta" dateTime={certificate.completed}>{completionDate(certificate.completed)}</time>
                <span className="cert-cta">View certificate <FiArrowRight className="icon-right" aria-hidden="true" /></span>
              </div>
            </Link>
          </div>
        ))}
      </div>
      {selected && <CertificateDialog certificate={selected} onClose={() => navigate("/", { replace: true })} />}
    </section>
  );
}
